import axios from "axios";
// eslint-disable-next-line no-unused-vars
import { convertDateToUTC, convertTimeToUTC } from "../utils/formatters"; // Must convert to UTC before sending, don't delete
import Cookies from "js-cookie";

// export const API_BASE_URL = "http://localhost:8080/api";
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + "/api";

export const contractApi = {
  /**
   * @property {Function} createContract - Creates a new contract for an organization
   * @param {string} officeCode - Organization's office code
   * @param {Object} contractDTO - Contract data transfer object
   * @returns {Promise} Axios promise with the created contract
   * @remarks
   * Impact on Child Lanes:
   * - No automatic lane creation - This operation does NOT create any transportation lanes automatically
   * - Manual association required - Lanes must be created separately using the `/lanes` endpoint
   * - Ownership binding - When creating lanes, they must explicitly reference contract's ID through the `contractId` field
   * - Existing lane isolation - Pre-existing lanes will not be associated with the new contract unless explicitly updated
   *
   * Typical Workflow:
   * 1. Create contract using this endpoint
   * 2. Use `POST /api/organizations/{officeCode}/lanes` to create lanes
   * 3. Specify contract's ID in lane's `contractId` field
   */
  createContract: (officeCode, contractDTO) =>
    axios.post(
      `${API_BASE_URL}/organizations/${officeCode}/contracts`,
      contractDTO
    ),

  getContractById: (officeCode, contractId) =>
    axios.get(
      `${API_BASE_URL}/organizations/${officeCode}/contracts/${contractId}`
    ),

  refreshRateShoppingGraph: (officeCode, operationDate) => {
    if (!officeCode) {
      return Promise.reject(new Error("officeCode is required to refresh rate shopping graph"));
    }

    const token = Cookies.get("authToken");
    const opDate = operationDate || new Date().toISOString().slice(0, 10);

    return axios.post(
      `${API_BASE_URL}/organizations/${officeCode}/rate-shopping/graph/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          operationDate: opDate,
        },
      }
    );
  },

  getContracts: (filters) => {

    const token = Cookies.get("authToken");
    const officeCodesCookie = Cookies.get("officeCodes");
    const officeCodes = officeCodesCookie ? officeCodesCookie.split(",") : [];
    const officeCode = officeCodes[0];

    const params = {
      officeCodes: officeCodes.join(","),
      page: filters?.page || 0,
      size: filters?.size || 15,
      sort: filters?.sort || "officeCode,asc",
      contractCode: filters?.contractCode,
      statuses: filters?.statuses?.join(","),
      effectiveStart: convertDateToUTC(filters?.effectiveStart),
      effectiveEnd: convertDateToUTC(filters?.effectiveEnd),
      expireStart: convertDateToUTC(filters?.expireStart),
      expireEnd: convertDateToUTC(filters?.expireEnd),
    };

    return axios.get(`${API_BASE_URL}/organizations/${officeCode}/contracts`, {
      headers: {
        Authorization: `Bearer ${token}`,
        //"Office-Code": officeCodes[0] || "",
      },
      params,
      paramsSerializer: { indexes: null },
    });
  },

  searchContracts: (filters) => {
    const token = Cookies.get("authToken");
    const officeCodes = (Cookies.get("officeCodes") || "").split(",").filter(Boolean);

    // Map your UI filters to SearchRequest
    const criteria = {
      // prefer an explicit filters.query; otherwise fall back to contractCode as the search term
      query: filters?.query ?? filters?.contractCode ?? null,
      // controller expects a single status; if UI has an array, take the first
      status: filters?.status ?? (Array.isArray(filters?.statuses) ? filters.statuses[0] : null),
    };

    const params = {
      page: filters?.page ?? 0,
      size: filters?.size ?? 15,
      sortBy: filters?.sortBy ?? "updatedAt",
      ascending: filters?.ascending ?? false,
    };

    return axios.post(
      `${API_BASE_URL}/partial-details/contract-search`,
      criteria,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          // Spring binds comma-separated header to List<String>
          "Office-Code": officeCodes.join(","),
        },
        params,
        paramsSerializer: { indexes: null },
      }
    );
  },


  updateContract: (officeCode, contractId, contractDTO) =>
    axios.put(
      `${API_BASE_URL}/organizations/${officeCode}/contracts/${contractId}`,
      { ...contractDTO, id: contractId }
    ),

  /**
   * @property {Function} deleteContract - Deletes an existing contract for an organization
   * @param {string} officeCode - Organization's office code
   * @param {string} contractId - ID of the contract to delete
   * @returns {Promise} Axios promise with the deletion result
   * @remarks
   * PERMANENTLY removes the contract and ALL associated lanes, routes, rates, fuel surcharges. This is a cascading deletion that cannot be undone.
   */
  deleteContract: (officeCode, contractId) =>
    axios.delete(
      `${API_BASE_URL}/organizations/${officeCode}/contracts/${contractId}`
    ),
};
